import {
  AFFECTATION_STATUSES,
  STATUS_COLOR,
  STATUS_ICON,
} from '@/components/Affectations/affectation.utils';
import { getAffectationColumns } from '@/components/Affectations/affectationColumns';
import DecisionActionIcon from '@/components/Affectations/DecisionActionIcon';
import UserAffectationsByStatus from '@/components/Affectations/UserAffectationsByStatus';
import useAffectationDecision, {
  DECISION_KEYS,
  decisionColor,
} from '@/components/Affectations/useAffectationDecision';
import type { AffectationCountsByUser } from '@/features/api/endpoints/affectationEndpoints';
import type { Affectation } from '@/models/types';
import { getUserLabel } from '@/shared/utils/user.utils';
import { Badge, Button, Group, Paper, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconChecks, IconX } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import isObject from 'lodash/isObject';
import { DataTable } from 'mantine-datatable';

type Props = {
  userId: string;
  user: Affectation['user'];
  /** Demandes en attente (`draft`) de l'utilisateur. */
  affectations: Affectation[];
  /** Compteurs des autres statuts, pour les onglets. */
  counts?: AffectationCountsByUser[string];
};

/** Carte d'un utilisateur : ses demandes en attente à valider, et l'historique par statut. */
const AdminUserAffectationsCard = ({ userId, user, affectations, counts }: Props) => {
  const { t } = useTranslate();
  const { onDecisionClick, isUpdating } = useAffectationDecision(userId);

  const isAnyUpdating = affectations.some(isUpdating);

  const getTabCount = (status: Affectation['status']): number =>
    status === 'draft' ? affectations.length : (counts?.[status] ?? 0);

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="xs" align="baseline">
            <Title order={2} size="h5">
              {getUserLabel(user, t('admin.affectations.unknownUser'))}
            </Title>
            {isObject(user) && user.email ? (
              <Text size="sm" c="dimmed">
                {user.email}
              </Text>
            ) : null}
          </Group>
          <Badge color="yellow" variant="light">
            {t('admin.affectations.count', { count: affectations.length })}
          </Badge>
        </Group>

        <Tabs defaultValue={affectations.length > 0 ? 'draft' : 'published'} keepMounted={false}>
          <Tabs.List>
            {AFFECTATION_STATUSES.map(status => {
              const Icon = STATUS_ICON[status];
              return (
                <Tabs.Tab
                  key={status}
                  value={status}
                  color={STATUS_COLOR[status]}
                  leftSection={<Icon size={14} />}
                  rightSection={
                    <Badge size="xs" variant="filled" circle color={STATUS_COLOR[status]}>
                      {getTabCount(status)}
                    </Badge>
                  }
                >
                  {t(`profil.affectations.status.${status}`)}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          <Tabs.Panel value="draft" pt="sm">
            <Stack gap="sm">
              <Group gap="xs" justify="flex-end">
                {(['approve', 'reject'] as const).map(decision => (
                  <Button
                    key={decision}
                    size="xs"
                    variant="light"
                    color={decisionColor(decision)}
                    leftSection={
                      decision === 'approve' ? <IconChecks size={16} /> : <IconX size={16} />
                    }
                    disabled={isAnyUpdating || affectations.length === 0}
                    onClick={() => onDecisionClick(affectations, decision, 'all')}
                  >
                    {t(DECISION_KEYS[decision].all.action)}
                  </Button>
                ))}
              </Group>

              <DataTable
                striped
                highlightOnHover
                withTableBorder
                records={affectations}
                idAccessor="id"
                noRecordsText={t('profil.affectations.empty')}
                columns={[
                  ...getAffectationColumns(t),
                  {
                    accessor: 'actions',
                    title: t('profil.affectations.columns.actions'),
                    textAlign: 'right',
                    render: (affectation: Affectation) => (
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {(['approve', 'reject'] as const).map(decision => (
                          <DecisionActionIcon
                            key={decision}
                            decision={decision}
                            disabled={isUpdating(affectation)}
                            onClick={() => onDecisionClick([affectation], decision, 'one')}
                          />
                        ))}
                      </Group>
                    ),
                  },
                ]}
              />
            </Stack>
          </Tabs.Panel>

          {AFFECTATION_STATUSES.filter(status => status !== 'draft').map(status => (
            <Tabs.Panel key={status} value={status} pt="sm">
              <UserAffectationsByStatus userId={userId} status={status} />
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>
    </Paper>
  );
};

export default AdminUserAffectationsCard;
